import { useEffect, useRef, useState } from 'react';
import { ArrowDown, Loader2, MessageSquare, User } from 'lucide-react';
import type { Danmaku } from '@/types';
import { getDanmakuBySession, subscribeToSessionDanmaku } from '@/lib/api';
import { Button } from './ui/button';

interface DanmakuStreamProps {
  sessionId: string;
  initialDanmaku?: Danmaku[];
}

export default function DanmakuStream({ sessionId, initialDanmaku = [] }: DanmakuStreamProps) {
  const [danmakuList, setDanmakuList] = useState<Danmaku[]>(initialDanmaku);
  const [isLoading, setIsLoading] = useState(!initialDanmaku.length);
  const [error, setError] = useState<string | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialDanmaku.length > 0) {
      setDanmakuList(initialDanmaku);
      setIsLoading(false);
      return;
    }

    const loadDanmaku = async () => {
      try {
        setIsLoading(true);
        setError(null);
        setDanmakuList(await getDanmakuBySession(sessionId, 1000));
      } catch (loadError) {
        console.error('加载弹幕失败:', loadError);
        setError('弹幕数据暂时无法加载');
      } finally {
        setIsLoading(false);
      }
    };
    if (sessionId) void loadDanmaku();
  }, [sessionId, initialDanmaku]);

  useEffect(() => {
    if (!sessionId) return;
    setIsSubscribed(true);
    const channel = subscribeToSessionDanmaku(sessionId, (newDanmaku: Danmaku) => {
      setDanmakuList((previous) => [...previous, newDanmaku]);
    });
    return () => {
      channel.unsubscribe();
      setIsSubscribed(false);
    };
  }, [sessionId]);

  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [danmakuList, autoScroll]);

  const handleScroll = () => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    setAutoScroll(scrollHeight - scrollTop - clientHeight < 100);
  };

  const jumpToLatest = () => {
    setAutoScroll(true);
    if (containerRef.current) containerRef.current.scrollTop = containerRef.current.scrollHeight;
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return Number.isNaN(date.getTime())
      ? '--:--:--'
      : date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  if (isLoading) {
    return <div className="flex min-h-56 items-center justify-center gap-3 text-sm text-[var(--text-muted)]"><Loader2 className="h-5 w-5 animate-spin text-[var(--action-blue)]" /><span>加载弹幕中...</span></div>;
  }

  if (error) {
    return <div role="alert" className="console-alert-error px-4 py-3 text-sm">{error}</div>;
  }

  return (
    <div>
      <div className="mb-3 flex min-h-10 items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
          <span className={isSubscribed ? 'h-1.5 w-1.5 rounded-full bg-[var(--status-healthy)]' : 'h-1.5 w-1.5 rounded-full bg-[var(--status-unknown)]'} />
          {isSubscribed ? '实时订阅已连接' : '实时订阅未连接'}
        </p>
        {!autoScroll && (
          <Button type="button" variant="outline" size="sm" onClick={jumpToLatest}>
            <ArrowDown className="h-4 w-4" />跳转到最新
          </Button>
        )}
      </div>

      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="h-[520px] overflow-y-auto rounded-md border border-[var(--border-subtle)] bg-[var(--panel-deep)] md:h-[600px]"
        role="log"
        aria-label="弹幕消息"
        aria-live="polite"
      >
        {danmakuList.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <MessageSquare className="h-8 w-8 text-[var(--border-strong)]" />
            <p className="mt-3 text-sm text-[var(--text-soft)]">等待新弹幕...</p>
            <p className="mt-1 text-xs text-[var(--text-muted)]">新弹幕出现后会自动追加到这里。</p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--border-subtle)]">
            {danmakuList.map((danmaku) => (
              <article key={danmaku.id} className="flex gap-3 px-3 py-3 transition-colors hover:bg-[var(--surface-hover)] md:px-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[var(--structural-border)] bg-[var(--panel-white)]">
                  <User className="h-3.5 w-3.5 text-[var(--text-muted)]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="text-xs font-medium text-[var(--text-soft)]">{danmaku.user_name || '匿名用户'}</span>
                    {danmaku.medal_name && (
                      <span className="rounded border border-[var(--vip-border)] bg-[var(--vip-bg)] px-1.5 py-0.5 text-[10px] text-[var(--vip-text)]">
                        {danmaku.medal_name} Lv{danmaku.medal_level}
                      </span>
                    )}
                    <time className="font-mono text-[10px] text-[var(--text-muted)]">{formatTime(danmaku.created_at)}</time>
                  </div>
                  <p className="mt-1 break-words text-[13px] leading-6 text-[var(--text-row)]">{danmaku.message}</p>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
